import graphql__1c7943fc58f5a6166523 from "./__generated__/VoteButton_votable.graphql.js";import { Component } from "react";
import { graphql, useFragment } from "react-relay";
import { injectIntl, defineMessages } from "react-intl";
import classNames from "classnames";

import withAlert from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import Tooltip from "../Tooltip/Tooltip";
import Button from "../Buttons/Button";
import decrementSize from "../Utils/decrementSize";
import { buttonWithAuthorization } from "../buttonWithAuthorization";

import { submitVote } from "../../mutations/SubmitVoteMutation";
import { useFormatMessage } from "../Intl/Context";
import { isUnauthorized } from "../Utils/authorizations";

class VoteButton extends Component {
  static defaultProps = {
    size: "md"
  };

  constructor(props) {
    super(props);
    this.onClick = this.onClick.bind(this);
    this.onVoteResponse = this.onVoteResponse.bind(this);

    this.state = { status: null };

    // If the calling component wants a ref to the current VoteButton component
    // we provide it through the ref. This is not an explicit forwardRef use but acts the
    // same.
    if (this.props.buttonRef) {
      this.props.buttonRef(this);
    }
  }

  get labels() {
    const { formatMessage } = this.props.intl;

    return {
      successMessage: formatMessage(intlMessages.successMessage),
      voteRemovedMessage: formatMessage(intlMessages.voteRemovedMessage)
    };
  }

  get isSubmitting() {
    return this.state.status === "submitting";
  }

  get disabled() {
    return this.isSubmitting || this.props.disabled;
  }

  get unauthorizedMessage() {
    return this.props.votable.authorizations.unauthorizedMessages.submitVote;
  }

  get className() {
    return classNames([
      "input-group",
      "btn-with-counter",
      "vote-button",
      this.props.className
    ]);
  }

  get buttonClassName() {
    return classNames([
      "btn",
      `btn-block-${this.props.size}`,
      "form-control",
      this.props.buttonClassName
    ]);
  }

  get counterClassName() {
    const { size } = this.props;
    const paddingSize = decrementSize(size);

    return classNames([
      "vote-button-counter",
      "input-group-addon",
      `text-metric-${size}`,
      `padding-x-${paddingSize}`
    ]);
  }

  // TODO : Improve this with Relay's refetch container system instead of
  // passing an explicit {authorization} parameter as argument of this method
  //
  onClick(e) {
    switch (e.authorization || this.props.authorization) {
      case "AUTHORIZED":
        this.setState({ status: "submitting" });

        submitVote(this.props.votable, this.props.voteType, this.onVoteResponse);
        break;

      case "UNAUTHORIZED":
        this.props.showAlert({
          message: this.unauthorizedMessage,
          type: ALERT_TYPE_ERROR
        });
        break;

      default:
        this.props.onClick(e);
    }
  }

  onVoteResponse(response) {
    if (response.submitVote.success) {
      this.setState({ status: "responseReceived" });

      const voteRemoved = response.submitVote.votable?.currentViewerVote === null;

      this.props.showAlert({
        message: voteRemoved
          ? this.labels.voteRemovedMessage
          : this.labels.successMessage,
        type: ALERT_TYPE_SUCCESS
      });
    } else {
      this.setState({ status: "responseError" });

      this.props.showAlert({
        message: response.submitVote.notice,
        type: ALERT_TYPE_ERROR
      });
    }
  }

  render() {
    const {
      ariaLabel,
      screenReaderLabel,
      label,
      labelClassName,
      iconClassName,
      votesCount,
      votesCountScreenReaderLabel
    } = this.props;

    const {
      className,
      unauthorizedMessage,
      buttonClassName,
      onClick,
      isSubmitting,
      counterClassName
    } = this;

    const voteAllowed = !isUnauthorized(this.props.authorization);
    const tooltipContent = voteAllowed ? this.props.tooltipContent : unauthorizedMessage;
    const disabled = !voteAllowed || isSubmitting;

    return (
      <div className={className}>
        <Tooltip show={tooltipContent} content={tooltipContent} enableOnTouch={false}>
          <Button
            aria-label={ariaLabel}
            screenReaderLabel={screenReaderLabel}
            className={buttonClassName}
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-loading={isSubmitting}
            label={label}
            labelClassName={labelClassName}
            iconClassName={iconClassName}
            iconPosition="after"
            data-vote-button
          />
        </Tooltip>

        <span className={counterClassName}>
          {votesCount}

          {votesCountScreenReaderLabel && (
            <span className="sr-only">{votesCountScreenReaderLabel}</span>
          )}
        </span>
      </div>
    );
  }
}

export const useRemainingVotesScreenReaderLabel = props => {
  const { remainingVotesCount, limitedVotesPerUser } = useFragment(
    VoteButton_votable,
    props.votable
  );

  const t = useFormatMessage();

  if (!limitedVotesPerUser) return null;

  return t(intlMessages.remainingVotesCount, { count: remainingVotesCount });
};

const VoteButton_votable = graphql__1c7943fc58f5a6166523;

export default injectIntl(buttonWithAuthorization(withAlert(VoteButton)));

const intlMessages = defineMessages({
  successMessage: {
    id: "vote.success",
    defaultMessage: "Votre vote a bien été pris en compte !"
  },
  voteRemovedMessage: {
    id: "vote.removed",
    defaultMessage: "Votre vote a bien été retiré !"
  },
  remainingVotesCount: {
    id: "vote.remainingVotesCount",
    defaultMessage: `({count, plural,
      =0 {aucun vote restant}
      one {{count} vote restant}
      other {{count} votes restants}
    })`
  }
});
